<div class="did-web-generator">
  <form class="form-section" [formGroup]="formGroup" (ngSubmit)="generateDid()">
    <section class="form-block" aria-labelledby="did-configuration-heading">
      <div class="section-heading">
        <span>02 · DID-Konfiguration</span>
        <h3 id="did-configuration-heading">Öffentliche Identität</h3>
        <p>Domain und Schlüsselfragment bestimmen, unter welcher Adresse die Identität erreichbar ist.</p>
      </div>

      <div class="identity-grid">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ 'did.forms.create.domain.name' | translate }}</mat-label>
          <span matTextPrefix>https://&nbsp;</span>
          <input matInput [formControl]="domain" placeholder="example.com" autocomplete="url">
          @if (getDomainValidationError().length > 0) {
          <mat-error>{{ getDomainValidationError() | translate }}</mat-error>
          }
          <mat-hint>Öffentlich erreichbarer Host ohne Protokoll.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ 'did.forms.create.key_suffix.name' | translate }}</mat-label>
          <span matTextPrefix>#&nbsp;</span>
          <input matInput [formControl]="keySuffix" [placeholder]="'did.forms.create.key_suffix.name' | translate"
            autocomplete="off">
          @if (getKeySuffixValidationError().length > 0) {
          <mat-error>{{ getKeySuffixValidationError() | translate }}</mat-error>
          }
          <mat-hint>Optionales Fragment mit 5 bis 20 Zeichen.</mat-hint>
        </mat-form-field>
      </div>
    </section>

    <div class="section-divider"></div>

    <section class="form-block certificate-block" [formGroup]="certificate" aria-labelledby="certificate-heading">
      <div class="section-heading certificate-heading">
        <div>
          <span>03 · Vertrauensnachweis</span>
          <h3 id="certificate-heading">Zertifikatsquelle</h3>
          <p>Wählen Sie aus, wie das öffentliche Zertifikat für das DID-Dokument bereitgestellt wird.</p>
        </div>
        <span class="required-note">Pflichtangabe</span>
      </div>

      <mat-button-toggle-group class="source-selector" [value]="certificateType" [disabled]="inProgress"
        (change)="setCertificateType($event.value)" aria-label="Zertifikatsquelle auswählen">
        <mat-button-toggle value="file">
          <mat-icon>upload_file</mat-icon>
          {{ 'did.forms.create.certificate.file.file' | translate }}
        </mat-button-toggle>
        <mat-button-toggle value="url">
          <mat-icon>link</mat-icon>
          {{ 'did.forms.create.certificate.url.url' | translate }}
        </mat-button-toggle>
        <mat-button-toggle value="ssl">
          <mat-icon>verified_user</mat-icon>
          {{ 'did.forms.create.certificate.ssl.name' | translate }}
        </mat-button-toggle>
      </mat-button-toggle-group>

      <div class="source-panel" aria-live="polite">
        @if (certificateType === 'file') {
        <div class="source-content">
          <div class="source-copy">
            <span class="source-icon"><mat-icon>description</mat-icon></span>
            <div>
              <strong>{{ 'did.forms.create.certificate.file.name' | translate }}</strong>
              <p>{{ 'did.forms.create.certificate.file.hint' | translate }}</p>
            </div>
          </div>

          <div class="file-picker">
            <input #fileInput type="file" accept=".pem,.crt" hidden (change)="onCertificateSelected($event)">
            <button mat-stroked-button color="primary" type="button" [disabled]="inProgress"
              (click)="clickFileInput(fileInput)">
              <mat-icon>attach_file</mat-icon>
              {{ 'did.forms.create.certificate.file.choose' | translate }}
            </button>
            <span class="file-state" [class.has-file]="getFileName().length > 0">
              <mat-icon>{{ getFileName().length > 0 ? 'check_circle' : 'info' }}</mat-icon>
              {{ getFileName() || 'Noch keine Datei ausgewählt' }}
            </span>
          </div>

          @if (getFileValidationError().length > 0) {
          <p class="source-error"><mat-icon>error_outline</mat-icon>{{ getFileValidationError() | translate }}</p>
          }
        </div>
        } @else if (certificateType === 'url') {
        <div class="source-content url-source">
          <div class="source-copy">
            <span class="source-icon"><mat-icon>language</mat-icon></span>
            <div>
              <strong>{{ 'did.forms.create.certificate.url.name' | translate }}</strong>
              <p>Das Zertifikat wird über eine sichere HTTPS-Adresse geladen.</p>
            </div>
          </div>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ 'did.forms.create.certificate.url.name' | translate }}</mat-label>
            <span matTextPrefix>https://&nbsp;</span>
            <input matInput [formControl]="url" placeholder="example.com/certificate.pem" autocomplete="url"
              (change)="resolveUrl()">
            <button mat-icon-button matSuffix type="button" [disabled]="inProgress || url.invalid"
              aria-label="Zertifikat laden" (click)="resolveUrl()">
              <mat-icon>download</mat-icon>
            </button>
            @if (getUrlValidationError().length > 0) {
            <mat-error>{{ getUrlValidationError() | translate }}</mat-error>
            }
            <mat-hint>Host und Pfad ohne Protokoll eingeben.</mat-hint>
          </mat-form-field>
        </div>
        } @else {
        <div class="source-content ssl-source">
          <div class="source-copy">
            <span class="source-icon"><mat-icon>lock</mat-icon></span>
            <div>
              <strong>SSL-Zertifikat der Domain</strong>
              <p>Das öffentlich präsentierte Zertifikat wird direkt von der angegebenen Domain übernommen.</p>
            </div>
          </div>

          <div class="ssl-status" [class.ready]="ssl.value && content.valid">
            <mat-icon>{{ ssl.value && content.valid ? 'verified' : 'hourglass_top' }}</mat-icon>
            <div>
              <strong>{{ ssl.value && content.valid ? 'Zertifikat geladen' : 'Domain wird geprüft' }}</strong>
              <span>{{ domain.value ? 'https://' + domain.value : 'Bitte zuerst eine gültige Domain angeben.' }}</span>
            </div>
          </div>
        </div>
        }
      </div>
    </section>

    <footer class="form-actions">
      <div class="security-note">
        <mat-icon>lock</mat-icon>
        <span>Der private Schlüssel wird sicher erzeugt und nicht im Browser gespeichert.</span>
      </div>
      <button mat-flat-button color="primary" type="submit" [disabled]="inProgress || formGroup.invalid">
        <mat-icon [class.spinning]="generating">{{ generating ? 'sync' : 'fingerprint' }}</mat-icon>
        {{ generating ? ('common.loading' | translate) : ('did.forms.generate' | translate) }}
      </button>
    </footer>
  </form>
</div>

@if (popUpObj !== null) {
<app-pop-up [popUpObj]="popUpObj"></app-pop-up>
}
@if (generating) {
<app-spinner [message]="'common.loading'"></app-spinner>
}